import React from 'react';
import { Box, Container, Typography, Button, Grid, Paper, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { CheckCircle, ArrowForward, Payment, EventNote, Videocam, LocalShipping, MedicationLiquid } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const Telemedicine = () => {
  const { t } = useTranslation();

  const benefits = [
    { id: 1, text: t('telemedicine.benefits.benefit1') },
    { id: 2, text: t('telemedicine.benefits.benefit2') },
    { id: 3, text: t('telemedicine.benefits.benefit3') },
    { id: 4, text: t('telemedicine.benefits.benefit4') },
    { id: 5, text: t('telemedicine.benefits.benefit5') },
    { id: 6, text: t('telemedicine.benefits.benefit6') }
  ];

  const steps = [
    { 
      id: 1, 
      title: t('telemedicine.process.step1.title'), 
      description: t('telemedicine.process.step1.description'),
      icon: <Payment fontSize="large" color="primary" />,
      action: (
        <Button 
          component="a"
          href="https://www.paypal.com/ncp/payment/CR99CJH6ZWK2S"
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="primary"
          size="medium"
          startIcon={<Payment />}
          sx={{ 
            mt: 2,
            bgcolor: '#0070ba', 
            '&:hover': { bgcolor: '#005ea6' } 
          }}
        >
          {t('telemedicine.payWithPayPal')}
        </Button>
      )
    },
    { 
      id: 2, 
      title: t('telemedicine.process.step2.title'), 
      description: t('telemedicine.process.step2.description'),
      icon: <EventNote fontSize="large" color="primary" />,
      action: (
        <Button 
          component={Link} 
          to="/booking" 
          variant="contained" 
          color="primary" 
          size="medium"
          sx={{ mt: 2 }}
        >
          {t('telemedicine.scheduleButton')}
        </Button>
      )
    },
    { 
      id: 3, 
      title: t('telemedicine.process.step3.title'), 
      description: t('telemedicine.process.step3.description'),
      icon: <Videocam fontSize="large" color="primary" />
    },
    { 
      id: 4, 
      title: t('telemedicine.process.step4.title'), 
      description: t('telemedicine.process.step4.description'),
      icon: <MedicationLiquid fontSize="large" color="primary" />
    },
    { 
      id: 5, 
      title: t('telemedicine.process.step5.title'), 
      description: t('telemedicine.process.step5.description'),
      icon: <LocalShipping fontSize="large" color="primary" />
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h2" component="h1" align="center" gutterBottom>
        {t('telemedicine.title')}
      </Typography>
      
      <Typography variant="h5" align="center" color="textSecondary" paragraph>
        {t('telemedicine.subtitle')}
      </Typography>
      
      <Box sx={{ my: 6 }}>
        <Typography variant="h4" gutterBottom>
          {t('telemedicine.benefitsTitle')}
        </Typography>
        <Paper elevation={3} sx={{ p: 4, bgcolor: 'background.paper' }}>
          <Grid container spacing={3}>
            {benefits.map((benefit) => (
              <Grid item xs={12} md={6} key={benefit.id}>
                <Box display="flex" alignItems="flex-start">
                  <CheckCircle color="primary" sx={{ mr: 2, mt: 0.5 }} />
                  <Typography variant="body1">{benefit.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>

      <Box sx={{ my: 6, textAlign: 'center' }}>
        <img 
          src="/images/telemedcine.png" 
          alt={t('telemedicine.imageAlt')} 
          style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }} 
        />
      </Box>
      
      <Box sx={{ my: 6 }}>
        <Typography variant="h4" gutterBottom>
          {t('telemedicine.processTitle')}
        </Typography>
        <Paper elevation={3} sx={{ p: 4, bgcolor: 'background.paper' }}>
          <List>
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                  <ListItemIcon sx={{ mt: 1 }}>
                    {step.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="h6" component="div">
                        {step.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                          {step.description}
                        </Typography>
                        {step.action && (
                          <Box sx={{ mt: 2 }}>
                            {step.action}
                          </Box>
                        )}
                      </>
                    }
                  />
                </ListItem>
                {index < steps.length - 1 && (
                  <Box display="flex" justifyContent="center" my={2}>
                    <ArrowForward color="action" />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Box>
      
      <Box sx={{ my: 6 }}>
        <Typography variant="h4" gutterBottom align="center">
          {t('telemedicine.herbsTitle')}
        </Typography>
        <Paper elevation={3} sx={{ p: 4, bgcolor: 'background.paper' }}>
          <Typography variant="body1" paragraph>
            {t('telemedicine.herbsDescription')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('telemedicine.shippingInfo')}
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default Telemedicine;